import { escapeFilterValue } from '../../lib/pocketbase'
import { getClientUser } from '../clientAuth'
import { getClientPb } from '../../lib/clientPocketbase'
import { loadClientDealTitles, dealTitle } from './deals'
import { isPreview, readOnlyGuard } from './previewMode'
import type {
  ClientMessage,
  ClientDocument,
  CreateMessageInput,
  CreateDocumentInput,
  MessageThread,
} from '../../types/clientPortal'

export async function getClientMessages(dealId?: string | null): Promise<ClientMessage[]> {
  const user = getClientUser()
  if (!user) throw new Error('Client not authenticated')

  let filter = `client_user_id = "${escapeFilterValue(user.id)}"`
  if (dealId) {
    filter += ` && deal_id = "${escapeFilterValue(dealId)}"`
  }

  const clientPb = getClientPb()
  const records = await clientPb.collection('client_messages').getFullList({
    filter,
    sort: 'created',
    expand: 'agent_user_id',
  })
  await loadClientDealTitles()

  return records.map(transformMessage)
}

export async function getClientMessageThreads(): Promise<MessageThread[]> {
  const messages = await getClientMessages()

  const threadMap = new Map<string | null, ClientMessage[]>()

  for (const msg of messages) {
    const key = msg.deal_id
    if (!threadMap.has(key)) {
      threadMap.set(key, [])
    }
    threadMap.get(key)!.push(msg)
  }

  const threads: MessageThread[] = []
  for (const [dealId, msgs] of threadMap) {
    const unreadCount = msgs.filter((m) => !m.read && m.sender_type === 'agent').length
    const lastMessage = msgs.length > 0 ? msgs[msgs.length - 1] : null
    const dealTitle = lastMessage?.expand?.deal_id?.title ?? null

    threads.push({
      deal_id: dealId,
      deal_title: dealTitle,
      messages: msgs,
      unread_count: unreadCount,
      last_message: lastMessage,
    })
  }

  threads.sort((a, b) => {
    const aDate = a.last_message?.created ?? ''
    const bDate = b.last_message?.created ?? ''
    return bDate.localeCompare(aDate)
  })

  return threads
}

export async function sendClientMessage(input: CreateMessageInput): Promise<ClientMessage> {
  readOnlyGuard('sending a message as them')
  const user = getClientUser()
  if (!user) throw new Error('Client not authenticated')

  const clientPb = getClientPb()
  const record = await clientPb.collection('client_messages').create({
    agency_id: user.agency_id,
    client_user_id: user.id,
    deal_id: input.deal_id || null,
    content: input.content,
    sender_type: 'client',
    read: false,
  })

  return transformMessage(record)
}

export async function markMessagesRead(messageIds: string[]): Promise<void> {
  // Called on open, not by anyone pressing anything: an agent looking at the
  // portal must not mark the athlete's mail as read on their behalf, and
  // must not see the page fail because it tried.
  if (isPreview()) return
  const user = getClientUser()
  if (!user) throw new Error('Client not authenticated')

  const clientPb = getClientPb()
  await Promise.all(
    messageIds.map((id) =>
      clientPb.collection('client_messages').update(id, {
        read: true,
        read_at: new Date().toISOString(),
      })
    )
  )
}

/**
 * Everything on the athlete's records page, from both collections.
 *
 * Until Sep 2026 this read `client_documents` alone. That collection holds
 * links the athlete typed in and nothing else, so an agency ticking "visible
 * in the client portal" on a real file — an executed contract, a W-9 — put
 * it somewhere the athlete had no page for. The file reached them only if it
 * hung off a contract they could also read. The page meanwhile promised
 * "every document shared with you".
 *
 * So both are read and merged. The `documents` side is the agency's store;
 * its list rule already limits a portal token to this client's rows, ticked
 * visible, and consults show_contracts for contract paperwork — nothing is
 * re-checked here that is not enforced there.
 *
 * The two reads are deliberately not Promise.all'd into a silent partial
 * result: if either collection fails, the athlete is told their records
 * could not be loaded rather than shown half of them as if that were all.
 */
export async function getClientDocuments(dealId?: string | null): Promise<ClientDocument[]> {
  const user = getClientUser()
  if (!user) throw new Error('Client not authenticated')

  const clientPb = getClientPb()
  const dealClause = dealId ? ` && deal_id = "${escapeFilterValue(dealId)}"` : ''
  const mine = `client_id = "${escapeFilterValue(user.client_id)}"`

  const [ownRecords, agencyRecords] = await Promise.all([
    clientPb.collection('client_documents').getFullList({
      filter: `${mine}${dealClause}`,
      sort: '-created',
    }),
    clientPb.collection('documents').getFullList({
      filter: `${mine} && visible_to_client = true${dealClause}`,
      sort: '-created',
      fields: 'id,agency_id,client_id,deal_id,name,type,file,file_url,file_name,file_size,created,updated',
    }),
  ])

  await loadClientDealTitles()

  return [...ownRecords.map(transformDocument), ...agencyRecords.map(transformAgencyDocument)]
    .sort((a, b) => b.created.localeCompare(a.created))
}

/**
 * A link that opens one of these documents.
 *
 * A link row is already a URL. A file is behind a protected field on either
 * collection, so the URL carries a token minted from this portal session: it
 * expires, and it cannot be forwarded to somebody who was never given access.
 */
export async function getPortalDocumentUrl(doc: ClientDocument): Promise<string> {
  if (doc.url) return doc.url

  const clientPb = getClientPb()
  const collection = doc.source === 'agency' ? 'documents' : 'client_documents'
  const record = await clientPb.collection(collection).getOne(doc.id)
  if (!record.file) throw new Error('This document has no file to open.')
  const token = await clientPb.files.getToken()
  return clientPb.files.getUrl(record, record.file as string, { token })
}

/** Matches PocketBase's own default limit, and the agency-side panel's. */
const MAX_UPLOAD_BYTES = 50 * 1024 * 1024

/**
 * The athlete files something of their own — a link, or a file off their
 * phone. Their uploads land in `client_documents`, never in the agency's
 * `documents` store: that one is the agency's record of them, and a portal
 * token has no write on it by design.
 */
export async function addClientDocument(input: CreateDocumentInput): Promise<ClientDocument> {
  readOnlyGuard('adding a document for them')
  const user = getClientUser()
  if (!user) throw new Error('Client not authenticated')

  const url = input.url?.trim() || null
  const file = input.file ?? null
  if (!url && !file) throw new Error('Add a file or a link.')
  if (url && file) throw new Error('Add a file or a link, not both.')
  if (file && file.size > MAX_UPLOAD_BYTES) {
    throw new Error(`${file.name} is larger than the 50 MB limit.`)
  }

  const clientPb = getClientPb()
  const fields: Record<string, string> = {
    agency_id: user.agency_id,
    client_id: user.client_id,
    title: input.title,
    document_type: input.document_type || '',
    description: input.description || '',
    shared_by_agent: 'false',
  }
  if (input.deal_id) fields.deal_id = input.deal_id
  if (url) fields.url = url

  let record
  if (file) {
    const form = new FormData()
    for (const [k, v] of Object.entries(fields)) form.append(k, v)
    form.append('file', file)
    form.append('file_name', file.name)
    form.append('file_size', String(file.size))
    record = await clientPb.collection('client_documents').create(form)
  } else {
    record = await clientPb.collection('client_documents').create(fields)
  }

  return transformDocument(record)
}

/**
 * The athlete removes something they added.
 *
 * Their own rows only — the collection rule enforces that, and a document
 * the agency shared is the agency's record to withdraw, not theirs.
 */
export async function deleteClientDocument(documentId: string): Promise<void> {
  readOnlyGuard('removing their document')
  const user = getClientUser()
  if (!user) throw new Error('Client not authenticated')
  await getClientPb().collection('client_documents').delete(documentId)
}

export function subscribeToMessages(
  callback: (message: ClientMessage) => void
): () => void {
  const user = getClientUser()
  if (!user) throw new Error('Client not authenticated')

  const clientPb = getClientPb()
  clientPb.collection('client_messages').subscribe('*', (e) => {
    if (e.action === 'create' && e.record.client_user_id === user.id && e.record.sender_type === 'agent') {
      callback(transformMessage(e.record))
    }
  })

  return () => {
    clientPb.collection('client_messages').unsubscribe('*')
  }
}

export async function getUnreadMessageCount(): Promise<number> {
  const user = getClientUser()
  if (!user) throw new Error('Client not authenticated')

  const clientPb = getClientPb()
  const result = await clientPb.collection('client_messages').getList(1, 1, {
    filter: `client_user_id = "${escapeFilterValue(user.id)}" && sender_type = "agent" && read = false`,
  })
  return result.totalItems
}

function transformMessage(record: any): ClientMessage {
  return {
    id: record.id,
    agency_id: record.agency_id,
    client_user_id: record.client_user_id,
    agent_user_id: record.agent_user_id,
    deal_id: record.deal_id,
    content: record.content,
    sender_type: record.sender_type,
    read: record.read ?? false,
    read_at: record.read_at,
    created: record.created,
    updated: record.updated,
    expand: {
          agent_user_id: record.expand?.agent_user_id
            ? {
                id: record.expand.agent_user_id.id,
                first_name: record.expand.agent_user_id.first_name,
                last_name: record.expand.agent_user_id.last_name,
                email: record.expand.agent_user_id.email,
                avatar: record.expand.agent_user_id.avatar,
              }
            : undefined,
          deal_id: dealTitle(record.deal_id),
        },
  }
}

/** A row the athlete filed themselves: a link, or a file they uploaded. */
function transformDocument(record: any): ClientDocument {
  return {
    id: record.id,
    source: 'you',
    agency_id: record.agency_id,
    client_id: record.client_id,
    deal_id: record.deal_id,
    title: record.title,
    url: record.url || null,
    file_name: record.file ? record.file_name || record.file : null,
    file_size: record.file_size ?? null,
    document_type: record.document_type,
    shared_by_agent: record.shared_by_agent ?? false,
    description: record.description,
    created: record.created,
    updated: record.updated,
    expand: { deal_id: dealTitle(record.deal_id) },
  }
}

/**
 * A row from the agency's own store that somebody ticked as visible.
 *
 * `documents` names things differently — `name`, and a `type` drawn from the
 * agency's own nine-value list rather than the portal's five. Only the types
 * that mean the same thing on both sides are carried across; anything else
 * is left null rather than forced into a bucket it does not belong in, so
 * the athlete's type filter never mislabels a file.
 */
function transformAgencyDocument(record: any): ClientDocument {
  return {
    id: record.id,
    source: 'agency',
    agency_id: record.agency_id,
    client_id: record.client_id,
    deal_id: record.deal_id || null,
    title: record.name || record.file_name || '—',
    // A row with no stored file may still carry a plain link.
    url: record.file ? null : record.file_url || null,
    file_name: record.file ? record.file_name || record.file : null,
    file_size: record.file_size ?? null,
    document_type: AGENCY_TYPE_MAP[record.type] ?? null,
    shared_by_agent: true,
    description: null,
    created: record.created,
    updated: record.updated,
    expand: { deal_id: dealTitle(record.deal_id) },
  }
}

/** Agency document types that map cleanly onto the portal's five. */
const AGENCY_TYPE_MAP: Record<string, ClientDocument['document_type']> = {
  contract: 'contract',
  player_agreement: 'contract',
  team_agreement: 'contract',
  endorsement_deal: 'contract',
  nda: 'contract',
  compliance_document: 'other',
  marketing_agreement: 'other',
  invoice: 'other',
  other: 'other',
}
